import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import DateAnnotation from "@shared/models/date-annotation"
import type { Group, GroupDateAnnotation } from "@shared/models/group"
import type SchoolYear from "@shared/models/school-year"
import { CURRENT_SCHOOL_YEAR_ID } from "@shared/models/school-year"
import { objectId } from "@shared/util/object-id"
import { handleError } from "cc-frontend/error-handling"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import { prepareGroupDateAnnotationCreate } from "cc-frontend/lib/actions/group/GROUP_DATE_ANNOTATION_CREATE"
import { prepareGroupDateAnnotationDelete } from "cc-frontend/lib/actions/group/GROUP_DATE_ANNOTATION_DELETE"
import { prepareGroupDateAnnotationUpdate } from "cc-frontend/lib/actions/group/GROUP_DATE_ANNOTATION_UPDATE"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import { use } from "ember-could-get-used-to-this"
import { cloneDeep, sortBy, toString } from "lodash-es"
import moment from "moment"
import styles from "./calendar.module.css"

const ALL_YEARS = "all"

interface Signature {
  Args: {
    group: Group
  }
}

export default class SchoolEditCalendar extends Component<Signature> {
  readonly styles = styles

  @service("ember-store") declare store: EmberStore

  @service declare dispatcher: Dispatcher

  @service declare modalManager: ModalManager

  @tracked selectedSchoolYearId: string | null = null

  @computed("args.group.attributes.cachedAncestry.[]")
  get ancestorGroupIds() {
    return this.args.group.attributes.cachedAncestry.map((ancestor) => ancestor.attributes.groupId)
  }

  @use ancestors = new FindManyAltogether(() => ["group", this.ancestorGroupIds])

  // Use an intermediate property to let us watch deeply-nested arrays.
  // See https://github.com/ember-cli/eslint-plugin-ember/blob/master/docs/rules/no-deeply-nested-dependent-keys-with-each.md
  @computed("ancestors.content.[]")
  get ancestorDateAnnotationsArrays() {
    return (
      this.ancestors.content?.map((ancestor) => ancestor.attributes.calendar.dateAnnotations) ?? []
    )
  }

  @computed(
    "args.group.attributes.calendar.dateAnnotations.[]",
    "ancestorDateAnnotationsArrays.@each.[]"
  )
  get planbookDateAnnotations() {
    const ourDateAnnotations = this.args.group.attributes.calendar.dateAnnotations.map((event) => ({
      event,
      isEditable: true,
    }))
    const ancestorDateAnnotations =
      this.ancestorDateAnnotationsArrays
        ?.flatMap((dateAnnotations) => dateAnnotations)
        .map((event) => ({ event, isEditable: false })) ?? []
    const dateAnnotations = ourDateAnnotations.concat(ancestorDateAnnotations)
    return sortBy(dateAnnotations, (annotation) => annotation.event.attributes.startDate)
  }

  get allYearsId() {
    return ALL_YEARS
  }

  get eventSchoolYearIds() {
    const ids = new Set<string>()
    for (const { event } of this.planbookDateAnnotations) {
      if (event.attributes.schoolYearId) ids.add(event.attributes.schoolYearId)
    }
    return [...ids].sort().reverse()
  }

  @use schoolYears = new FindManyAltogether(() => ["school-year", this.eventSchoolYearIds])

  get schoolYearFilterOptions(): Array<{ id: string; label: string }> {
    const loaded = this.schoolYears.content
    return this.eventSchoolYearIds.map((id) => {
      const schoolYear = loaded?.find((year) => year.id === id)
      return { id, label: schoolYear?.attributes.shortAbbreviation ?? id }
    })
  }

  get showSchoolYearFilter() {
    return this.schoolYearFilterOptions.length > 1
  }

  get activeSchoolYearId() {
    if (this.selectedSchoolYearId) return this.selectedSchoolYearId
    const ids = this.eventSchoolYearIds
    if (ids.includes(CURRENT_SCHOOL_YEAR_ID)) return CURRENT_SCHOOL_YEAR_ID
    return ids[0] ?? ALL_YEARS
  }

  get filteredDateAnnotations() {
    if (this.activeSchoolYearId === ALL_YEARS) return this.planbookDateAnnotations
    return this.planbookDateAnnotations.filter(
      ({ event }) => event.attributes.schoolYearId === this.activeSchoolYearId
    )
  }

  @action
  selectSchoolYear(schoolYearId: string) {
    this.selectedSchoolYearId = schoolYearId
  }

  @action
  async addAnEvent() {
    const initialSchoolYear: SchoolYear = await this.store.find(
      "school-year",
      CURRENT_SCHOOL_YEAR_ID
    )
    const firstDay = toString(initialSchoolYear.attributes.years[0]).concat("-01-01")

    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      allowNoClassToggle: true,
      datesAreEditable: true,
      description: null,
      title: null,
      startDate: null,
      endDate: null,
      minimumStartDate: firstDay,
      modalTitle: "Add Event",
      submitButtonText: "Create",
      dateIs: "FLEXIBLE_RANGE",
      submitAction: ({ title, description, startDate, endDate, isOff }) => {
        endDate = endDate ?? startDate
        const schoolYearId = inferSchoolYearId(startDate)
        const annotation = new DateAnnotation({
          id: objectId(),
          type: "date-annotation-group",
          attributes: {
            title,
            description,
            startDate: dateFns.format(startDate, "YYYY-MM-DD"),
            endDate: dateFns.format(endDate, "YYYY-MM-DD"),
            isOff: Boolean(isOff),
            schoolYearId,
            isPublished: true,
          },
        }) as unknown as GroupDateAnnotation
        this.dispatcher.dispatchPreparedAction(
          prepareGroupDateAnnotationCreate({ annotation, group: this.args.group })
        )
      },
    })
  }

  @action
  async openEditAnnotationModal(oldAnnotation: GroupDateAnnotation) {
    const initialSchoolYear: SchoolYear = await this.store.find(
      "school-year",
      CURRENT_SCHOOL_YEAR_ID
    )
    const firstDay = toString(initialSchoolYear.attributes.years[0]).concat("-01-01")

    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      allowNoClassToggle: true,
      dateIs: "FLEXIBLE_RANGE",
      datesAreEditable: true,
      description: oldAnnotation.attributes.description,
      classIsOff: oldAnnotation.attributes.isOff,
      endDate: oldAnnotation.attributes.endDate,
      minimumStartDate: firstDay,
      modalTitle: "Edit Event",
      startDate: oldAnnotation.attributes.startDate,
      title: oldAnnotation.attributes.title,
      submitButtonText: "Update",
      submitAction: ({ title, description, startDate, endDate, isOff }) => {
        endDate = endDate ?? startDate
        const schoolYearId = inferSchoolYearId(startDate)
        let newAnnotation = cloneDeep(oldAnnotation)
        newAnnotation.attributes.title = title
        newAnnotation.attributes.description = description
        newAnnotation.attributes.schoolYearId = schoolYearId
        newAnnotation.attributes.startDate = startDate
        newAnnotation.attributes.endDate = endDate
        newAnnotation.attributes.isOff = Boolean(isOff)
        this.dispatcher.dispatchPreparedAction(
          prepareGroupDateAnnotationUpdate({
            annotation: newAnnotation,
            group: this.args.group,
          })
        )
      },
    })
  }

  @action
  deleteEvent(annotation: GroupDateAnnotation) {
    swal({
      title: "Remove Event?",
      text: "This will remove it from your teachers' planbooks.",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.dispatcher.dispatchPreparedAction(
          prepareGroupDateAnnotationDelete({
            annotation,
            group: this.args.group,
          })
        )
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

function inferSchoolYearId(startDate: string) {
  // TODO: Have the user specify the school year.
  // For now, we infer it from the start date, using July 1 - June 30 as the school year (like Spikey.SchoolYear.get_id_from_datetime).
  const startDateMoment = moment(startDate)
  const schoolYearNum =
    startDateMoment.month() < 6 ? startDateMoment.year() - 1 : startDateMoment.year()
  return `SY${schoolYearNum}${schoolYearNum + 1}`
}
